import { EMPTY, Observable, of, of as observableOf } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EmptyAction, NevisAdminAction } from '../actions';
import { GetUserError, GetUserSuccess, GetUserSuperAdminSuccess, Login, LoginComplete, LoginError, LoginPasswordExpired, UserActionTypes } from './user.actions';
import { NavigationService } from '../../navbar/navigation.service';
import { UserData, UserToken } from './user.model';
import { UserService } from '../../user/login/user.service';
import { AssignedPermission } from '../permissions/permissions.model';
import { Store } from '@ngrx/store';
import { AppState } from '../reducer';
import { LoginPayloadModel } from './login-payload.model';
import { PermissionsHelper } from '../../permissions/permissions.helper';
import { ChangePasswordService } from '../../username-menu/change-password.service';
import { HttpErrorResponse } from '@angular/common/http';


@Injectable()
export class UserEffects {
   private actions$ = inject<Actions<NevisAdminAction<any>>>(Actions);
   private store$ = inject<Store<AppState>>(Store);
   private userService = inject(UserService);
   private navigationService = inject(NavigationService);
   private changePasswordService = inject(ChangePasswordService);

   authenticate$: Observable<LoginComplete | LoginError | LoginPasswordExpired>;
   getUser$: Observable<GetUserSuccess | GetUserError>;

  // TODO: switched off to be able to run under BE doesnt have authentication
   getUserError$: Observable<void>;
   logoutEffect$: Observable<EmptyAction>;
   openExpiredPasswordDialog$: Observable<void>;

   getUserSuperAdmin$: Observable<GetUserSuperAdminSuccess> = createEffect(() => this.actions$
    .pipe(ofType(UserActionTypes.LoginComplete, UserActionTypes.GetUserSuccess))
    .pipe(
      map((action: LoginComplete | GetUserSuccess) => {
        return (action.payload as any).user ? (action.payload as LoginComplete['payload']).user : action.payload as GetUserSuccess['payload'];
      }),
      switchMap((userData: UserData) => {
        return this.userService.getUserPermissionsV2(userData.userKey)
          .pipe(
            map((assignedPermissions: AssignedPermission[]) => {
              const isSuperAdmin: boolean = PermissionsHelper.hasGlobalSuperAdmin(assignedPermissions);
              return new GetUserSuperAdminSuccess(isSuperAdmin);
            }),
          );
      })
    ));

  constructor() {
    const actions$ = this.actions$;
    const userService = this.userService;
    const navigationService = this.navigationService;


    this.authenticate$ = createEffect(() => actions$
      .pipe(ofType(UserActionTypes.Login))
      .pipe(
        map((action: Login) => action.payload),
        switchMap(({username, password}: LoginPayloadModel) => {
          return userService.login(username, password)
            .pipe(
              mergeMap((userID: UserToken) => {
                return userService.getUserData()
                  .pipe(map((userData: UserData) => new LoginComplete({user: userData, userId: userID})));
              }),
              catchError((error: HttpErrorResponse) => {
                if (error.status === 403 && error.error?.error === 'PASSWORD_EXPIRED') {
                  return observableOf(new LoginPasswordExpired(username));
                }
                return observableOf(new LoginError('Username or password is incorrect, please try again'));
              })
            );
        })
      ));


    this.getUser$ = createEffect(() => this.actions$
      .pipe(ofType(UserActionTypes.GetUser))
      .pipe(
        switchMap(() => userService.getUserData()
          .pipe(
            map((res: UserData) => new GetUserSuccess(res)),
            catchError((error) => of(new GetUserError(error)))
          )
        )
      ));

    this.getUserError$ = createEffect(() => actions$
      .pipe(
        ofType(UserActionTypes.GetUserError, UserActionTypes.LoginError),
        switchMap((): Observable<void> => {
          navigationService.navigateToLogin();
          return EMPTY;
        })
      ), {dispatch: false});

    this.openExpiredPasswordDialog$ = createEffect(() => actions$
      .pipe(
        ofType(UserActionTypes.LoginPasswordExpired),
        switchMap((action: LoginPasswordExpired): Observable<void> => {
          this.changePasswordService.openExpiredPasswordDialog(action.payload);
          return EMPTY;
        })
      ), {dispatch: false});
  }
}
